import {
  getExtensionId,
  getCookies,
  createWindow,
  waitOnRemoveWindow,
  removeWindow,
  webRequest,
  checkExtensionVersion,
  type Cookie,
} from ".";
import { wait } from "src/utils/extension/ServiceClient";
import { toast } from "react-toastify";
import type { VintedSessionRefreshTriggerReason } from "src/interfaces/event";
import {
  createVintedRefreshRunId,
  recordVintedSessionRefreshAborted,
  recordVintedSessionRefreshSucceeded,
  recordVintedSessionRefreshTriggered,
} from "./vintedSessionRefreshEvents";
import {
  needsVintedSessionPopup,
  vintedSessionPopupReason,
} from "./vintedSession";

class NotLoggedInError extends Error {
  response: unknown;

  constructor(response: unknown) {
    super(
      "There was an error, please log out from Vinted and log into it again."
    );
    this.response = response;
  }
}

export async function getVintedCookies(extensionId: string): Promise<Cookie[]> {
  return await getCookies(extensionId, {
    domain: ".vinted.com",
  });
}

export const RequiredExtensionVersion = "1.0.8";

export function checkVintedRequiredExtensionVersion() {
  return checkExtensionVersion(RequiredExtensionVersion);
}

const RequiredCookies = ["refresh_token_web", "access_token_web"];

function cookiesForStorage(cookies: Cookie[]): Cookie[] {
  return cookies.filter((c) => c.name && c.value);
}

function getUserIdFromCookies(cookies: Cookie[]): string | null {
  for (const name of RequiredCookies) {
    const token = cookies.find((c) => c.name === name)?.value;
    if (token) {
      const userId = getUserIdFromToken(token);
      if (userId) return userId;
    }
  }
  return null;
}

// Vinted issues access/refresh cookies even for logged-out guest sessions, so
// the mere presence of the cookies isn't enough — a genuine login is one where
// the token actually decodes to a user id.
function isLoggedInVinted(cookies: Cookie[]): boolean {
  return (
    RequiredCookies.every((name) =>
      cookies.some((c) => c.name === name && c.value)
    ) && getUserIdFromCookies(cookies) != null
  );
}

export function getUserIdFromToken(token: string): string | null {
  try {
    const segment = token.split(".")[1];
    const payload = JSON.parse(atob(segment));
    if (payload.act?.sub != null) return String(payload.act.sub);
    if (payload.sub != null) return String(payload.sub);
  } catch (_) {
    // fall through
  }
  return null;
}

function parseInitialUserFromHtml(html: string): {
  id: string;
  login: string;
} | null {
  return parseInitialUserFromMarker(html, '\\"initialUserState\\"');
}

function parseInitialUserFromMarker(
  html: string,
  marker: string
): {
  id: string;
  login: string;
} | null {
  const idx = html.indexOf(marker);
  if (idx === -1) return null;

  const start = html.indexOf("{", idx + marker.length);
  if (start === -1) return null;

  let depth = 0;
  let end = start;
  for (; end < html.length; end++) {
    if (html[end] === "{") depth++;
    else if (html[end] === "}") {
      depth--;
      if (depth === 0) break;
    }
  }

  try {
    const source = html.slice(start, end + 1);
    const json = JSON.parse(`"${source}"`);
    const parsed = JSON.parse(json);
    const user = parsed?.user;
    if (user?.id != null && user?.login) {
      return { id: String(user.id), login: String(user.login) };
    }
  } catch (_) {
    const source = html.slice(idx);
    const normalized = source.replace(/\\"/g, '"');
    const idMatch = normalized.match(/"id"\s*:\s*(\d+)/);
    const loginMatch = normalized.match(/"login"\s*:\s*"([^"]+)"/);
    if (idMatch?.[1] && loginMatch?.[1]) {
      return { id: idMatch[1], login: loginMatch[1] };
    }
  }

  return null;
}

async function performVintedLogin(extensionId: string): Promise<boolean> {
  const windowsCreateResponse = await createWindow(extensionId, {
    focused: true,
    type: "popup",
    url: "https://www.vinted.com/member/signup/select_type",
  });

  const windowId = windowsCreateResponse.result;
  let closed = false;

  await Promise.any([
    waitOnRemoveWindow(extensionId, { windowId }),
    new Promise<void>((resolve) => {
      const check = async () => {
        if (closed) return resolve();
        const cookies = await getVintedCookies(extensionId);

        if (isLoggedInVinted(cookies)) {
          try {
            // Polling loop: never open a refresh popup on top of the login
            // window — getVintedUser retries with recovery once this resolves.
            if (await fetchCurrentUser(extensionId, {
              allowSessionRefresh: false,
            })) {
              removeWindow(extensionId, { windowId });
              return resolve();
            }
          } catch (e) {
            console.warn(
              "User cookies found, but no user in response",
              (e as NotLoggedInError).response
            );
          }
        }

        setTimeout(() => check(), 1000);
      };

      check();
    }),
  ]);
  closed = true;

  const cookies = await getVintedCookies(extensionId);
  try {
    if (
      isLoggedInVinted(cookies) &&
      (await fetchCurrentUser(extensionId, { allowSessionRefresh: false }))
    ) {
      return true;
    }
  } catch (e) {
    console.warn(
      "User cookies found, but no user in response",
      (e as NotLoggedInError).response
    );
  }

  return false;
}

const SessionRefreshPopupTimeoutMs = 60_000;
const SessionRefreshPollMs = 1_000;

/** Shared so parallel Vinted requests only open one session-refresh popup. */
let sessionRefreshInFlight: Promise<void> | null = null;

/** User closed the refresh window, or tokens never rotated before timeout. */
export class VintedSessionRefreshAbortedError extends Error {
  reason: "user-closed" | "timeout";

  constructor(
    reason: "user-closed" | "timeout",
    message = "Vinted session refresh was cancelled or timed out."
  ) {
    super(message);
    this.name = "VintedSessionRefreshAbortedError";
    this.reason = reason;
  }
}

/**
 * Open a tiny unfocused Vinted window so the real page JS can refresh cookies.
 * Extension fetch cannot do this — `/session-refresh` has no Set-Cookie.
 * Rejects if the user closes the window before cookies refresh, or on timeout.
 */
export async function refreshVintedSessionWithPopup(
  extensionId: string,
  {
    reason = "homepage-session-refresh",
  }: { reason?: VintedSessionRefreshTriggerReason } = {}
): Promise<void> {
  if (sessionRefreshInFlight) {
    await sessionRefreshInFlight;
    return;
  }

  sessionRefreshInFlight = (async () => {
    const refreshRunId = createVintedRefreshRunId();
    const startedAt = Date.now();
    recordVintedSessionRefreshTriggered({ refreshRunId, reason });
    console.warn("(vinted) opening session-refresh popup", { reason });
    const cookiesBefore = await getVintedCookies(extensionId);
    const accessBefore = cookiesBefore.find(
      (c) => c.name === "access_token_web"
    )?.value;

    toast.info(
      "Vinted website was opened in a small window to refresh your session."
    );

    const windowsCreateResponse = await createWindow(extensionId, {
      focused: false,
      type: "popup",
      url: "https://www.vinted.com/session-refresh?ref_url=%2F",
      width: 420,
      height: 560,
    });
    console.warn("(vinted) session-refresh popup response", {
      windowId: windowsCreateResponse?.result,
      error: windowsCreateResponse?.error,
    });
    const windowId = windowsCreateResponse.result;
    if (windowId == null) {
      recordVintedSessionRefreshAborted({
        refreshRunId,
        reason: "window-create-failed",
        triggerReason: reason,
        durationMs: Date.now() - startedAt,
      });
      throw new Error("Failed to open Vinted session refresh window");
    }

    let closed = false;
    const closeWindow = async () => {
      if (closed) return;
      closed = true;
      try {
        await removeWindow(extensionId, { windowId });
      } catch {
        // already closed
      }
    };

    let settled = false;
    try {
      await new Promise<void>((resolve, reject) => {
        const finish = (fn: () => void) => {
          if (settled) return;
          settled = true;
          fn();
        };

        waitOnRemoveWindow(extensionId, { windowId }).then(() => {
          closed = true;
          finish(() =>
            reject(new VintedSessionRefreshAbortedError("user-closed"))
          );
        });

        void (async () => {
          const deadline = Date.now() + SessionRefreshPopupTimeoutMs;
          while (Date.now() < deadline) {
            await wait(SessionRefreshPollMs);
            if (closed || settled) return;
            const cookies = await getVintedCookies(extensionId);
            const access = cookies.find(
              (c) => c.name === "access_token_web"
            )?.value;
            if (
              access &&
              access !== accessBefore &&
              isLoggedInVinted(cookies)
            ) {
              finish(() => resolve());
              return;
            }
          }
          finish(() => reject(new VintedSessionRefreshAbortedError("timeout")));
        })();
      });

      recordVintedSessionRefreshSucceeded({
        refreshRunId,
        viaPopup: true,
        reason,
        durationMs: Date.now() - startedAt,
      });
    } catch (e) {
      const abortReason =
        e instanceof VintedSessionRefreshAbortedError
          ? e.reason
          : ("error" as const);
      recordVintedSessionRefreshAborted({
        refreshRunId,
        reason: abortReason,
        triggerReason: reason,
        durationMs: Date.now() - startedAt,
        error: e,
      });
      throw e;
    } finally {
      await closeWindow();
    }
  })().finally(() => {
    sessionRefreshInFlight = null;
  });

  await sessionRefreshInFlight;
}

async function requestVintedHomepage(extensionId: string) {
  return await webRequest(extensionId, {
    url: "https://www.vinted.com/",
    init: {
      method: "GET",
      headers: {
        accept: "application/json,text/plain,*/*,image/webp",
        "accept-language": "en-US",
        locale: "en-US",
      },
    },
  });
}

/** Extension `webRequest` result carries the final URL after redirects. */
function homepageResult(response: unknown): {
  status: number;
  url?: string;
  data?: unknown;
} {
  const result = (
    response as {
      result?: { status?: number; url?: string; data?: unknown };
    }
  )?.result;
  return {
    status: result?.status ?? 0,
    url: result?.url,
    data: result?.data,
  };
}

async function fetchCurrentUser(
  extensionId: string,
  { allowSessionRefresh = true }: { allowSessionRefresh?: boolean } = {}
): Promise<{
  username: string;
  userId: string;
} | null> {
  let response = await requestVintedHomepage(extensionId);
  let user = parseInitialUserFromHtml(response?.result?.data ?? "");

  // Vinted 307s stale/guest sessions to /session-refresh (or /rate-limited),
  // where the homepage HTML has no user payload. Only the real page JS can
  // rotate the cookies, so reuse the same popup the sync path uses.
  if (!user && allowSessionRefresh) {
    const result = homepageResult(response);
    if (needsVintedSessionPopup(result)) {
      await refreshVintedSessionWithPopup(extensionId, {
        reason: vintedSessionPopupReason(result),
      });
      response = await requestVintedHomepage(extensionId);
      user = parseInitialUserFromHtml(response?.result?.data ?? "");
    }
  }

  if (user) {
    return { username: user.login, userId: user.id };
  }

  const cookies = await getVintedCookies(extensionId);
  const userId = getUserIdFromCookies(cookies);
  if (userId) return { username: "", userId };

  throw new NotLoggedInError(response);
}

export async function getVintedUser(): Promise<{
  username: string;
  userId: string;
  cookies: Cookie[];
}> {
  checkVintedRequiredExtensionVersion();

  const extensionId = getExtensionId();
  let cookies = await getVintedCookies(extensionId);

  if (!isLoggedInVinted(cookies)) {
    await performVintedLogin(extensionId);
    cookies = await getVintedCookies(extensionId);

    if (!isLoggedInVinted(cookies)) {
      throw new Error("Log into Vinted to allow My Reseller Genie to connect.");
    }
  }

  let username = "";
  let userId = "";

  try {
    const user = await fetchCurrentUser(extensionId);
    if (user) {
      username = user.username;
      userId = user.userId;
    }
  } catch (e) {
    if (!(e instanceof NotLoggedInError)) throw e;
  }

  if (!userId) {
    userId = getUserIdFromCookies(cookies) ?? "";
  }

  if (!userId) {
    throw new Error(
      "Unable to detect your Vinted user ID. Please log out of Vinted and log back in, then try again."
    );
  }

  if (!username) {
    throw new Error(
      "Unable to detect your Vinted username. Please visit vinted.com while logged in, then try again."
    );
  }

  return {
    username,
    userId,
    cookies: cookiesForStorage(cookies),
  };
}
